// FILE: components/cards/ExplorePathCard.tsx
// Card riêng cho /explore — chỉ sale mode (chưa mua), KHÔNG progress/owner/unlocked logic
// Khác PathCard: gọn hơn, focus eye-tracking → giá 💎 → tag → title → demo → CTA "Xem chi tiết"
"use client";

import HardNavLink from "@/components/shared/HardNavLink";
import UserHoverCard from "@/components/shared/UserHoverCard";
import { Users, BookOpen, Gem, Unlock, ArrowRight } from "lucide-react";

export interface ExplorePathCardProps {
  id: string;
  title: string;
  slug?: string;
  emoji?: string;
  gradient?: string;

  // Author
  author: string;
  authorId?: string;
  authorUsername?: string | null;

  // Tags pill
  orgName?: string;
  orgHref?: string;
  positionName?: string;
  positionHref?: string;

  // Stats
  students?: number;
  questions?: number;
  demoQuestions?: number;

  // Price (chỉ hiện 💎, không VND)
  price?: number | "free";

  onClick?: () => void;
}

export default function ExplorePathCard({
  id,
  title,
  slug,
  emoji = "📚",
  gradient = "from-[#0f3a4a] to-[#1a5570]",
  author,
  authorId,
  authorUsername,
  orgName,
  orgHref,
  positionName,
  positionHref,
  students = 0,
  questions = 0,
  demoQuestions = 0,
  price = "free",
  onClick,
}: ExplorePathCardProps) {
  const href = `/paths/${slug || id}`;
  const isFree = price === "free" || price === 0;

  const cardCls =
    "bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden " +
    "hover:shadow-md hover:-translate-y-1 hover:border-teal-400/40 " +
    "active:scale-[0.98] active:translate-y-0 " +
    "transition-all duration-200 cursor-pointer group flex flex-col";

  const content = (
    <>
      {/* ── COVER (≈40% chiều cao) — clickable navigate path detail ── */}
      <HardNavLink href={href} className={`h-32 bg-gradient-to-br ${gradient} relative flex items-center justify-center overflow-hidden`}>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(255,255,255,.1),transparent_60%)]" />
        <span className="text-5xl drop-shadow-[0_4px_12px_rgba(0,0,0,.25)] group-hover:scale-110 transition-transform duration-300">
          {emoji}
        </span>

        {/* Price badge — chỉ 💎, không VND */}
        <div className="absolute top-2.5 right-2.5">
          {isFree ? (
            <span className="px-2 py-1 rounded-full text-[9px] font-bold bg-emerald-500 text-white shadow-sm flex items-center gap-1">
              <Unlock className="w-3 h-3" /> Miễn phí
            </span>
          ) : (
            <span className="px-2 py-1 rounded-full text-[10px] font-bold bg-amber-500 text-white shadow-sm flex items-center gap-1">
              <Gem className="w-3 h-3" /> {price}
            </span>
          )}
        </div>
      </HardNavLink>

      {/* ── BODY ── */}
      <div className="p-4 flex flex-col flex-1">
        {/* Dòng 1: Tag pills (org + position) */}
        {(orgName || positionName) && (
          <div className="flex items-center gap-1.5 flex-wrap mb-2">
            {orgName && (orgHref ? (
              <HardNavLink
                href={orgHref}
                className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 hover:border-blue-400 transition-colors"
              >
                🏦 {orgName}
              </HardNavLink>
            ) : (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                🏦 {orgName}
              </span>
            ))}
            {positionName && (positionHref ? (
              <HardNavLink
                href={positionHref}
                className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-200 hover:bg-teal-100 hover:border-teal-400 transition-colors"
              >
                💼 {positionName}
              </HardNavLink>
            ) : (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-200">
                💼 {positionName}
              </span>
            ))}
          </div>
        )}

        {/* Dòng 2: Title (clickable → path detail) */}
        <HardNavLink href={href} className="block mb-1.5 flex-grow">
          <h3 className="text-[14px] font-bold text-slate-900 leading-snug line-clamp-2 group-hover:text-teal-700 transition-colors">
            {title}
          </h3>
        </HardNavLink>

        {/* Dòng 3: Author (UserHoverCard, không "by", không avatar inline) */}
        <div className="text-[11px] mb-2">
          {authorId ? (
            <UserHoverCard
              userId={authorUsername || authorId}
              name={author}
              className="text-[11px] text-slate-500 hover:text-teal-700"
            />
          ) : (
            <span className="text-slate-500">{author}</span>
          )}
        </div>

        {/* Dòng 4: Meta gộp 1 dòng */}
        <div className="flex items-center gap-2 text-[11px] text-slate-500 mb-3">
          {students > 0 && (
            <span className="flex items-center gap-0.5">
              <Users className="w-3 h-3" />
              {students >= 1000 ? `${(students / 1000).toFixed(1)}k` : students}
            </span>
          )}
          {students > 0 && questions > 0 && <span className="text-slate-300">•</span>}
          {questions > 0 && (
            <span className="flex items-center gap-0.5">
              <BookOpen className="w-3 h-3" />
              {questions} câu
            </span>
          )}
        </div>

        {/* ── FOOTER ── */}
        <div className="flex items-center justify-between gap-2 pt-3 border-t border-slate-100 mt-auto">
          {/* Demo badge bên trái */}
          {demoQuestions > 0 ? (
            <span className="inline-flex items-center gap-1 px-2 py-[3px] rounded-full text-[9px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 shrink-0">
              <Unlock className="w-2.5 h-2.5" />
              {demoQuestions} câu demo
            </span>
          ) : (
            <span /> /* spacer giữ space-between */
          )}

          {/* CTA secondary — "Xem chi tiết" */}
          <HardNavLink
            href={href}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-[11px] font-bold hover:bg-blue-100 hover:border-blue-400 transition-colors shrink-0"
          >
            Xem chi tiết
            <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
          </HardNavLink>
        </div>
      </div>
    </>
  );

  // Card NEVER wraps as <a> để tránh nested <a> với pill org/position bên trong.
  // Click vùng có thể navigate: cover, title, CTA, pill org, pill position.
  if (onClick) {
    return (
      <div onClick={onClick} className={cardCls}>
        {content}
      </div>
    );
  }

  return (
    <div className={cardCls}>
      {content}
    </div>
  );
}
